import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Card, IconButton, InputAdornment, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import StyledChip from "@/components/atoms/Chip";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import useToast from "@/contexts/toast-context";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";

type Props = { messages: CampaignFormMessages };

function CitiesTags({ messages }: Props) {
  const { values, setFieldValue } = useFormikContext<CampaignFormData>();
  const [cities, setCities] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");
  const { showToast } = useToast();

  useEffect(() => {
    if (Array.isArray(values.segment.cities)) {
      setCities(values.segment.cities);
    }
  }, [values.segment.cities]);

  const handleAddCity = (newCity: string) => {
    const cityName = newCity.trim();
    const isCityValid = cityName !== "" && !cities.includes(cityName);

    if (isCityValid) {
      const updatedCities = [...cities, cityName];
      setCities(updatedCities);
      setFieldValue("segment.cities", updatedCities);
      setFieldValue("newCityTags", [...(values.newCityTags || []), cityName]);
      setSearch("");
    } else if (cities.includes(cityName)) {
      showToast({ text: messages.tagExistsError, type: "error" });
    }
  };

  const handleDelete = (cityToDelete: string) => {
    const updatedCities = cities.filter((city) => city !== cityToDelete);
    const filteredNewCities = (values.newCityTags || []).filter((city) => city !== cityToDelete);

    setCities(updatedCities);
    setFieldValue("segment.cities", updatedCities);
    setFieldValue("newCityTags", filteredNewCities);
  };

  const handleInputKeyDown = () => {
    if (!search || values.disabledField) return;

    const trimmedSearch = search.trim();

    if (trimmedSearch !== "" && !cities.includes(trimmedSearch)) {
      handleAddCity(trimmedSearch);
    } else if (cities.includes(trimmedSearch)) {
      showToast({ text: messages.tagExistsError, type: "error" });
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.citiesTagTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <TextField
                variant="standard"
                label={messages.citiesLabel}
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={values.disabledField}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && search && search.trim()) {
                    e.preventDefault();
                    handleInputKeyDown();
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end" sx={{ marginRight: "-10px" }}>
                        <IconButton
                          onClick={() => handleAddCity(search || "")}
                          disabled={search.trim() === "" || values.disabledField}
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },

                  inputLabel: { shrink: true },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12 }} sx={{ overflowY: "scroll", height: 100 }}>
              <MDBox display="flex" flexWrap="wrap" gap={1}>
                {cities.map((tag, index) => (
                  <StyledChip
                    key={`${index}-${tag}`}
                    variant="outlined"
                    label={tag}
                    deleteIcon={<HighlightOffIcon fontSize="medium" style={{ color: "white" }} />}
                    onDelete={() => handleDelete(tag)}
                    disabled={values.isConfirmedStatus}
                  />
                ))}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default CitiesTags;
